import { z } from 'zod';
import { getDefaultNodeVersion, WORKFLOW_NODE_ACTIONS } from '../../post_call_workflows/node-catalog';
import { configValidators, getConfigValidator } from '../../post_call_workflows/validators/validator_registry';
// Via reads and writes workflows as nested steps: each list runs top to bottom, and a step with branches ends its
// list, splitting into paths that each continue with their own steps. Stored workflows are trees of nodes with
// ids and children, rooted at a Start node; these functions convert between the two.
const STRING_OPERATIONS = [
    'equals',
    'not-equals',
    'contains',
    'not-contains',
    'greater-than',
    'less-than',
    'greater-than-or-equal',
    'less-than-or-equal',
    'any-of',
    'none-of',
    'exists',
    'not-exists'
];
export const workflowConditionSchema = z.object({
    type: z.enum(['string', 'number', 'boolean', 'array']).describe('How to compare the two sides'),
    value: z.string().describe('What to test, usually a reference such as "{{Look up policy.success}}"'),
    operation: z
        .enum(STRING_OPERATIONS)
        .describe('number: no contains/any-of; boolean: equals, not-equals, exists'),
    compareTo: z
        .union([z.string(), z.number(), z.boolean(), z.array(z.string())])
        .optional()
        .describe('What to compare against; leave out for exists and not-exists')
});
const nodeStepSchema = z.object({
    name: z
        .string()
        .min(1)
        .describe('Unique within the workflow. Later steps use its outputs as {{Name.field}}; no dots or braces.'),
    app: z.string().describe('From list_workflow_nodes, such as "Webhook" or "Strada"'),
    action: z.string().describe('From list_workflow_nodes, such as "Webhook" or "Response"'),
    version: z.number().optional().describe('Leave out for the version the editor uses for new steps'),
    config: z
        .record(z.string(), z.unknown())
        .optional()
        .describe('Its settings, as get_workflow_node describes')
});
const MAX_BRANCH_DEPTH = 3;
// Nested to a fixed depth rather than recursive, so the tool schema has no $refs.
const stepSchemaAtDepth = (depth) => depth === 0
    ? nodeStepSchema
    : z.union([
        nodeStepSchema,
        z.object({
            name: z.string().min(1).describe('A name for the split'),
            branches: z
                .array(z.object({
                name: z.string().min(1),
                when: z
                    .array(z.array(workflowConditionSchema))
                    .optional()
                    .describe('Runs when any group matches, where a group matches when all its conditions do. Leave out to always run. Every branch that matches runs, not just the first.'),
                steps: z.array(stepSchemaAtDepth(depth - 1))
            }))
                .min(1)
                .describe('Must be the last step in its list; what follows goes inside each branch')
        })
    ]);
export const workflowStepsSchema = z.array(stepSchemaAtDepth(MAX_BRANCH_DEPTH));
const isBranchStep = (step) => 'branches' in step;
const toStoredCondition = ({ type, value, operation, compareTo }) => ({
    type,
    objKey: value,
    operation,
    ...(compareTo !== undefined && { objValue: compareTo })
});
const fromStoredCondition = (condition) => {
    if (typeof condition !== 'object' || condition === null)
        return undefined;
    const { type, objKey, operation, objValue } = condition;
    const parsed = workflowConditionSchema.safeParse({ type, value: objKey, operation, compareTo: objValue });
    return parsed.success ? parsed.data : undefined;
};
const configOf = (node) => 'config' in node ? node.config : {};
/** The workflow as nested steps, leaving out its Start node. */
export const workflowToSteps = (workflow) => {
    if (!workflow)
        return [];
    const byId = new Map(workflow.nodes.map((node) => [node.id, node]));
    // Stored workflows are trees, but one whose nodes loop back would otherwise be walked until the tab runs out of
    // memory, so each node is read once
    const visited = new Set();
    const visit = (id) => {
        if (!id || visited.has(id))
            return undefined;
        visited.add(id);
        return byId.get(id);
    };
    const sequence = (firstId) => {
        const steps = [];
        let node = visit(firstId);
        while (node) {
            if (node.app === 'Flow-control' && node.actionType === 'Split') {
                steps.push({
                    name: node.name,
                    branches: node.children.flatMap((pathId) => {
                        const path = visit(pathId);
                        if (!path)
                            return [];
                        const conditions = configOf(path).conditions;
                        const when = Array.isArray(conditions)
                            ? conditions
                                .filter(Array.isArray)
                                .map((group) => group.flatMap((item) => fromStoredCondition(item) ?? []))
                                .filter((group) => group.length > 0)
                            : [];
                        return [{ name: path.name, ...(when.length > 0 && { when }), steps: sequence(path.children[0]) }];
                    })
                });
                return steps;
            }
            const config = configOf(node);
            steps.push({
                name: node.name,
                app: node.app,
                action: node.actionType,
                version: node.version,
                ...(Object.keys(config).length > 0 && { config })
            });
            node = visit(node.children[0]);
        }
        return steps;
    };
    return sequence(visit(workflow.root)?.children[0]);
};
// Fills in the defaults the editor's form would have saved for a new step, keeping every key the step set.
const withDefaults = (step, version, context) => {
    const config = step.config ?? {};
    if (!(`${step.app}-${step.action}-${version}` in configValidators))
        return config;
    const parsed = getConfigValidator(step.app, step.action, version, context === 'toolCall').safeParse(config);
    return parsed.success && typeof parsed.data === 'object' && parsed.data !== null
        ? { ...parsed.data, ...config }
        : config;
};
/**
 * Builds a stored workflow from nested steps. Every node gets a new id: stored node configs are immutable by id, so
 * reusing an id with a changed config would silently keep the old one.
 */
export const stepsToWorkflow = (steps, { context, previous, startName, newId = () => crypto.randomUUID() }) => {
    const nodes = [];
    const start = previous?.nodes.find(({ id }) => id === previous.root);
    // An edit elsewhere must not write defaults into steps that were saved without them.
    const hadStep = (step) => previous?.nodes.some((node) => node.name === step.name && node.app === step.app && node.actionType === step.action) ?? false;
    const push = (node) => {
        const created = { ...node, id: newId() };
        nodes.push(created);
        return created;
    };
    const build = (sequence) => {
        const ids = sequence.map((step, index) => {
            if (isBranchStep(step)) {
                if (index < sequence.length - 1) {
                    throw new Error(`"${step.name}" branches, so it must be the last step in its list. Move the steps after it into each branch.`);
                }
                const pathIds = step.branches.map((branch) => {
                    const firstId = build(branch.steps);
                    return push({
                        name: branch.name,
                        app: 'Flow-control',
                        actionType: 'Path',
                        version: 1,
                        children: firstId ? [firstId] : [],
                        config: { conditions: (branch.when ?? []).map((group) => group.map(toStoredCondition)) }
                    }).id;
                });
                return push({
                    name: step.name,
                    app: 'Flow-control',
                    actionType: 'Split',
                    version: 1,
                    children: pathIds,
                    config: {}
                }).id;
            }
            const version = step.version ?? getDefaultNodeVersion(step.app, step.action, context);
            return push({
                name: step.name,
                app: step.app,
                actionType: step.action,
                version,
                children: [],
                config: hadStep(step) ? (step.config ?? {}) : withDefaults(step, version, context)
            }).id;
        });
        ids.forEach((id, index) => {
            const next = ids[index + 1];
            const node = nodes.find((candidate) => candidate.id === id);
            if (node && next)
                node.children = [next];
        });
        return ids[0];
    };
    const firstId = build(steps);
    const root = {
        ...(start ?? {
            id: newId(),
            name: startName,
            app: 'Flow-control',
            actionType: 'Start',
            version: 1,
            config: {}
        }),
        children: firstId ? [firstId] : []
    };
    return { root: root.id, nodes: [root, ...nodes] };
};
const catalogEntry = (app, action) => WORKFLOW_NODE_ACTIONS.find((entry) => entry.app === app && entry.actionType === action);
export const isNodeAvailableIn = (app, action, context) => {
    const entry = catalogEntry(app, action);
    return Boolean(entry &&
        (!entry.availableIn || entry.availableIn.includes(context)) &&
        !entry.excludedFrom?.includes(context));
};
/** Namespaces a workflow of each kind can reference besides its own steps. */
const CONTEXT_NAMESPACES = {
    toolCall: ['action', 'agent', 'call', 'chat', 'metadata', 'secret', 'secureMetadata', 'sessionVariables'],
    postCall: ['call', 'chat', 'metadata', 'secret', 'sessionVariables'],
    inboundPreCall: ['agent', 'call', 'chat', 'lastOutbound', 'metadata', 'secret', 'sessionVariables'],
    outboundPreCall: ['callVariables', 'metadata', 'secret', 'sessionVariables'],
    sentinel: ['call', 'metadata', 'secret', 'sentinel'],
    actionStudio: ['action', 'secret', 'metadata', 'email', 'hubspot']
};
// Workflows an agent calls, which hand it back only what Return Response returns.
const RETURNS_RESPONSE = ['toolCall', 'actionStudio'];
/** Steps that end the conversation's turn some other way, so a tool-call path can end without Return Response. */
const ENDS_WITHOUT_RESPONSE = new Set([
    'Strada/Response',
    'Call/End-call',
    'Call/Transfer',
    'Call/Handoff',
    'Chat/Handoff',
    'Amazon-Connect/Transfer-to-flow'
]);
const INVALID_NAME_PATTERN = /[.{}|]|__pCt__/;
// A Return Response value that is a whole webhook response, such as {{Look up policy.responseBody}}.
const WHOLE_RESPONSE_PATTERN = /^\{\{([^.{}|]+)\.responseBody\}\}$/;
const responseFieldsOf = (config) => config &&
    typeof config === 'object' &&
    'response' in config &&
    config.response &&
    typeof config.response === 'object'
    ? Object.entries(config.response)
    : [];
const referencesIn = (value) => [...JSON.stringify(value ?? {}).matchAll(/\{\{([^{}]+)\}\}/g)].flatMap(([, reference]) => reference ? [reference] : []);
const decodeReferencePart = (part) => {
    try {
        return decodeURIComponent(part.replace(/__pCt__/g, '%'));
    }
    catch {
        return part;
    }
};
const pathOf = (reference) => (reference.split('|')[0] ?? '').split('.');
const rootOf = (reference) => decodeReferencePart(pathOf(reference)[0] ?? '');
// A step's own output sits under one of these, which says nothing to a reader.
const OUTPUT_ROOTS = new Set(['result', 'responseBody']);
/** Text with each {{Step.result.field}} written the way a person reads it: "Step › field". */
export const readableReferences = (text) => text.replace(/\{\{([^{}]+)\}\}/g, (_, reference) => {
    const [root = '', ...rest] = pathOf(reference).map((part) => decodeReferencePart(part.trim()));
    const field = OUTPUT_ROOTS.has(rest[0] ?? '') ? rest.slice(1) : rest;
    return field.length > 0 ? `${root} › ${field.join('.')}` : root;
});
/** The secrets something reads with {{secret.NAME}}, such as an agent's action workflows. */
export const secretNamesIn = (value) => [
    ...new Set(referencesIn(value).flatMap((reference) => {
        const [root, name] = pathOf(reference).map((part) => part.trim());
        return root === 'secret' && name ? [decodeReferencePart(name)] : [];
    }))
];
/** Problems that would stop a workflow publishing or running as intended. `where` names the workflow. */
export const checkWorkflowSteps = (steps, context, where) => {
    const issues = [];
    const names = new Map();
    const namespaces = CONTEXT_NAMESPACES[context] ?? [];
    const issue = (severity, step, message) => issues.push({ severity, where: `${where} › "${step}"`, message });
    const visit = (sequence, ancestors) => {
        const seen = [...ancestors];
        sequence.forEach((step, index) => {
            names.set(step.name, (names.get(step.name) ?? 0) + 1);
            if (INVALID_NAME_PATTERN.test(step.name)) {
                issue('error', step.name, 'Step names cannot contain dots, braces, pipes or "__pCt__".');
            }
            if (isBranchStep(step)) {
                if (step.branches.filter(({ when }) => !when?.length).length > 1) {
                    issue('info', step.name, 'More than one branch has no conditions; each of them always runs.');
                }
                step.branches.forEach((branch) => {
                    referencesIn(branch.when).forEach((reference) => checkReference(reference, branch.name, seen));
                    if (branch.steps.length === 0 && RETURNS_RESPONSE.includes(context)) {
                        issue('warning', branch.name, 'This branch has no steps, so the agent gets an empty result here.');
                    }
                    visit(branch.steps, [...seen, step.name]);
                });
                return;
            }
            const entry = catalogEntry(step.app, step.action);
            if (!entry) {
                issue('error', step.name, `There is no ${step.app}/${step.action} step. Check list_workflow_nodes.`);
            }
            else if (!isNodeAvailableIn(step.app, step.action, context)) {
                issue('error', step.name, `${entry.displayName} can't be used in this kind of workflow.`);
            }
            const version = step.version ?? getDefaultNodeVersion(step.app, step.action, context);
            const key = `${step.app}-${step.action}-${version}`;
            if (entry && !(key in configValidators)) {
                issue('error', step.name, `${entry.displayName} has no version ${version}.`);
            }
            else if (entry) {
                const result = getConfigValidator(step.app, step.action, version, context === 'toolCall').safeParse(step.config ?? {});
                if (!result.success) {
                    result.error.issues.forEach(({ path, message }) => issue('error', step.name, `${path.length ? `${path.join('.')}: ` : ''}${message}`));
                }
            }
            if (entry?.terminalIn?.includes(context) && index < sequence.length - 1) {
                issue('error', step.name, `${entry.displayName} ends the workflow, so nothing can come after it.`);
            }
            referencesIn(step.config).forEach((reference) => checkReference(reference, step.name, seen));
            if (RETURNS_RESPONSE.includes(context) && step.app === 'Strada' && step.action === 'Response') {
                responseFieldsOf(step.config).forEach(([field, value]) => {
                    const whole = typeof value === 'string' ? WHOLE_RESPONSE_PATTERN.exec(value.trim()) : null;
                    if (!whole)
                        return;
                    issue('warning', step.name, `"${field}" hands the agent everything "${whole[1]}" got back. Return only the fields it needs: all of it enters the conversation, personal details included.`);
                });
            }
            const isLast = index === sequence.length - 1;
            if (RETURNS_RESPONSE.includes(context) &&
                isLast &&
                !ENDS_WITHOUT_RESPONSE.has(`${step.app}/${step.action}`)) {
                issue('warning', step.name, 'This path ends without Return Response, so the agent gets an empty result ({}) when it ends here.');
            }
            seen.push(step.name);
        });
    };
    const checkReference = (reference, step, seen) => {
        if (reference !== reference.trim()) {
            issue('error', step, `{{${reference}}} has spaces inside the braces, so it won't be filled in.`);
            return;
        }
        const root = rootOf(reference);
        if (namespaces.includes(root) || seen.includes(root))
            return;
        issue('warning', step, `{{${reference}}} refers to "${root}", which isn't an earlier step on this path or something this workflow receives.`);
    };
    visit(steps, []);
    if (RETURNS_RESPONSE.includes(context) && steps.length === 0) {
        issues.push({ severity: 'error', where, message: 'The workflow has no steps, so calling it fails.' });
    }
    names.forEach((count, name) => {
        if (count > 1)
            issue('error', name, 'Another step has the same name; step names must be unique.');
    });
    return issues;
};
//# sourceMappingURL=workflow-steps.js.map